import React, { useState, useEffect, useRef } from 'react';
import { FaCheckCircle } from 'react-icons/fa';
import { MdErrorOutline } from 'react-icons/md';

interface EmailValidatedToCheckExistComponentProps {
    email: string;
    setEmail: (value: string) => void;
    className?: any;
    getEmail: (email: string) => Promise<boolean>;
    touched: boolean;
    setTouched: (touched: boolean) => void;
    setIsEmailExist?: (value: boolean | null) => void;
}

const EmailValidatedToCheckExistComponent = ({
    email,
    setEmail,
    getEmail,
    className = {
        parentDiv: "",
        label: "font-semibold text-sm",
        input: "w-full rounded py-2 px-4 mt-2 border-2 text-sm text-black outline-tertiary"
    },
    touched,
    setTouched,
    setIsEmailExist
}: EmailValidatedToCheckExistComponentProps) => {
    const [isEmailAvailable, setIsEmailAvailable] = useState<boolean | null>(null);
    const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
    const [initialEmail, setInitialEmail] = useState<string>('');
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setInitialEmail(email);
    }, [email]);

    useEffect(() => {
        let debounceTimer: NodeJS.Timeout;

        if (touched && email?.trim() !== '' && email?.trim() !== initialEmail) {
            debounceTimer = setTimeout(() => {
                checkUsernameAvailability(email?.trim());
            }, 1000);
        } else {
            setIsEmailAvailable(null);
            setShowErrorMessage(false);
        }

        return () => clearTimeout(debounceTimer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, touched]);

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setEmail(value);
        setTouched(true);
        setIsEmailAvailable(null);
        setIsEmailExist && setIsEmailExist(null);
        setShowErrorMessage(false);
        setShowSuccessMessage(false);
        const isValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/gm.test(value);
        e.target.setCustomValidity(isValid ? "" : "Please enter a valid email");
        e.target.reportValidity();
    };

    const checkUsernameAvailability = (email: string) => {
        getEmail(email)
            .then((response: boolean) => {
                setIsEmailAvailable(!response);
                setIsEmailExist && setIsEmailExist(response);
                setShowErrorMessage(response);
                if (!response) {
                    setShowSuccessMessage(true);
                    setTimeout(() => {
                        setShowSuccessMessage(false);
                    }, 3000);
                }
                if (inputRef.current) {
                    inputRef.current.setCustomValidity(response ? "Email is already exist" : "");
                }
            })
            .catch((error: Error) => {
                console.error('Error checking email availability:', error);
                setIsEmailAvailable(null);
                setShowErrorMessage(false);
                setShowSuccessMessage(false);
            });
    };

    return (
        <div className={className.parentDiv}>
            <label className={className.label}>Email*</label>
            <input
                ref={inputRef}
                name="email"
                type="email"
                required
                className={className.input}
                placeholder="Email"
                value={email}
                onChange={handleEmailChange}
                autoComplete="new-email"
            />
            {showSuccessMessage && isEmailAvailable === true && (
                <div className="flex items-center mt-2 text-xs text-green-500">
                    <FaCheckCircle className="mr-1" style={{ fontSize: 16 }} />
                    <span>Email is available</span>
                </div>
            )}
            {showErrorMessage && isEmailAvailable === false && (
                <div className="flex items-center mt-2 text-xs text-red-500">
                    <MdErrorOutline className="mr-1" style={{ fontSize: 16 }} />
                    <span>Email is already exist</span>
                </div>
            )}
        </div>
    );
};

export default EmailValidatedToCheckExistComponent;